/**
 * Sub-page 3: data quality.
 *
 * The rule editor is built from the operator catalogue the API serves, so the
 * form always offers exactly what the engine can run — adding an operator on
 * the server makes it appear here with no frontend change.
 */

import { useMemo, useState } from 'react'
import { EmptyState, Modal } from '@/shared/components'
import { useToast } from '@/shared/components/toast'
import { Field, SectionHeader, SeverityChip } from '../../components'
import { useDqConditionCatalogue, useDqMutations, useDqOperators } from '../../hooks'
import { ReorderableList } from '../../components/ReorderableList'
import type {
  DqCondition,
  DqDefinition,
  DqKind,
  DqOperator,
  DqReport,
  DqRule,
  DqSeverity,
  NodeTypeDetail,
} from '../../types/brain.types'

const KIND_HELP: Record<DqKind, string> = {
  VALIDATE: 'Checks a value. A failing ERROR rule holds the record back instead of letting it become golden.',
  INFER: 'Fills a value in when it is missing, before the checks run.',
  COMPUTE: 'Derives a value from other properties, overwriting what was there.',
  ACCESS: 'Hides a value when the record is read.',
}

/** A glyph and a tint per kind, so a rule's job is legible before it is read. */
const KIND_ICON: Record<DqKind, string> = {
  VALIDATE: '✓',
  INFER: '✦',
  COMPUTE: '∑',
  ACCESS: '⦿',
}
const KIND_TONE: Record<DqKind, string> = {
  VALIDATE: 'ok',
  INFER: 'accent',
  COMPUTE: 'accent',
  ACCESS: 'warn',
}

/**
 * The full order to send when one kind's group has been reordered.
 *
 * The panel groups rules by kind for readability, but the engine has one list
 * per node type. Reordering VALIDATE rules must not disturb where the INFER
 * ones sit, so the untouched groups keep their relative order and the edited
 * one is spliced back in at the positions it already occupied.
 */
function orderInFull(
  all: { rule_id: string; kind: string }[],
  kind: string,
  orderedIds: string[],
): string[] {
  const slots = all.map((r, index) => ({ index, kind: r.kind, id: r.rule_id }))
  const targets = slots.filter((s) => s.kind === kind).map((s) => s.index)
  const out = slots.map((s) => s.id)
  targets.forEach((slot, i) => {
    out[slot] = orderedIds[i]
  })
  return out
}

export function DqPanel({
  graphId,
  nodeTypeId,
  detail,
}: {
  graphId: string
  nodeTypeId: string
  detail: NodeTypeDetail
}) {
  const operators = useDqOperators(graphId)
  const { remove, update, reorder, test } = useDqMutations(graphId, nodeTypeId)
  const toast = useToast()

  const [editing, setEditing] = useState<DqRule | 'new' | null>(null)
  const [report, setReport] = useState<DqReport | null>(null)

  const byKind = useMemo(() => {
    const groups: Record<string, DqRule[]> = {}
    for (const rule of detail.dq_rules) (groups[rule.kind] ??= []).push(rule)
    return groups
  }, [detail.dq_rules])

  return (
    <div className="split" style={{ gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr)' }}>
      <div className="stack">
        <SectionHeader
          title="Data quality rules"
          hint="Run against every record of this type before it is resolved, in the order below. The first rule that rejects a record stops the stage — the rules after it are not evaluated, and the record says which one stopped it. A WARN is recorded and the record carries on."
          action={
            <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
              Add rule
            </button>
          }
        />

        {detail.dq_rules.length === 0 ? (
          <EmptyState
            title="No quality rules yet"
            action={
              <button className="btn btn-primary" onClick={() => setEditing('new')}>
                Add the first rule
              </button>
            }
          >
            Without rules, anything a source sends becomes a record — including rows with no name
            and addresses that are not addresses.
          </EmptyState>
        ) : (
          Object.entries(byKind).map(([kind, rules]) => (
            <div key={kind} className="stack" style={{ gap: 8 }}>
              <div className="row-tight">
                <span className="small" style={{ fontWeight: 640 }}>
                  {kind}
                </span>
                <span className="small faint">{KIND_HELP[kind as DqKind]}</span>
              </div>
              <ReorderableList
                items={rules.map((rule) => ({ ...rule, id: rule.rule_id }))}
                disabled={reorder.isPending}
                onReorder={(orderedIds) =>
                  // The list the API is sent is every rule on the node type, in
                  // its new order — including the kinds not shown in this group,
                  // whose relative order is untouched.
                  reorder
                    .mutateAsync(orderInFull(detail.dq_rules, kind, orderedIds))
                    .catch((error: unknown) => toast.error(error))
                }
                renderItem={(rule) => {
                const condition = readCondition(rule, operators.data ?? [])
                return (
                  <div className="rulecard" data-off={!rule.active}>
                    <div className="cp-rulehdr">
                      <span className="cp-ruleicon" data-tone={KIND_TONE[rule.kind]} aria-hidden="true">
                        {KIND_ICON[rule.kind]}
                      </span>
                      <span className="cp-ruletitle">
                        <b>{rule.name}</b>
                        <span>
                          {rule.kind} · {rule.property_key ?? 'whole record'}
                        </span>
                      </span>
                      {/* Severity is a VALIDATE concept, and for those it is
                          set on the control below rather than restated here. */}
                      {rule.kind !== 'VALIDATE' && <SeverityChip severity={rule.severity} />}
                      {!rule.active && <span className="badge badge-neutral">disabled</span>}
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() =>
                          update
                            .mutateAsync({ id: rule.rule_id, body: { active: !rule.active } })
                            .catch((error) => toast.error(error))
                        }
                      >
                        {rule.active ? 'Disable' : 'Enable'}
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditing(rule)}>
                        Edit
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={async () => {
                          if (!window.confirm(`Delete '${rule.name}'?`)) return
                          try {
                            await remove.mutateAsync(rule.rule_id)
                            toast.success('Rule deleted.')
                          } catch (error) {
                            toast.error(error)
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>

                    {rule.description && (
                      <p className="small muted" style={{ margin: 0 }}>
                        {rule.description}
                      </p>
                    )}

                    {/* The check, read left to right: property, test, value. */}
                    <div className="cp-cond">
                      <div className="cp-condrow">
                        <span className="cp-token">{condition.property}</span>
                        <span className="cp-token" data-v="op">
                          {condition.op}
                        </span>
                        <span className="cp-token" data-v={condition.value ? 'val' : 'empty'}>
                          {condition.value || 'no value needed'}
                        </span>
                      </div>

                      {/* What the rule decides. VALIDATE is the only kind that
                          ends in a verdict, so it is the only one that offers
                          the choice — the rest just say what they do. */}
                      {rule.kind === 'VALIDATE' ? (
                        <div className="cp-actionrow">
                          A record that matches is:
                          <div className="cp-seg" role="group" aria-label="Action">
                            {(['accept', 'reject'] as const).map((value) => (
                              <button
                                key={value}
                                className="cp-segbtn"
                                data-on={condition.action === value}
                                data-tone={value === 'accept' ? 'ok' : 'err'}
                                disabled={update.isPending}
                                aria-pressed={condition.action === value}
                                onClick={() =>
                                  update
                                    .mutateAsync({
                                      id: rule.rule_id,
                                      body: {
                                        definition: {
                                          ...(rule.definition ?? {}),
                                          action: value,
                                        },
                                      },
                                    })
                                    .catch((error) => toast.error(error))
                                }
                              >
                                {value === 'accept' ? 'Accepted' : 'Rejected'}
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="cp-actionrow">{KIND_HELP[rule.kind]}</div>
                      )}
                    </div>
                  </div>
                )
                }}
              />
            </div>
          ))
        )}
      </div>

      <RuleTester
        detail={detail}
        report={report}
        busy={test.isPending}
        onRun={async (record) => {
          try {
            setReport(await test.mutateAsync(record))
          } catch (error) {
            toast.error(error)
          }
        }}
      />

      {editing && (
        <DqRuleDialog
          graphId={graphId}
          nodeTypeId={nodeTypeId}
          detail={detail}
          existing={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

/**
 * A rule's definition, split into the three parts it reads as. The operator's
 * own label comes from the catalogue when the API has served it, so the row
 * says "is one of" rather than `in` — and falls back to the raw op when it has
 * not loaded yet rather than rendering a blank.
 */
function readCondition(
  rule: DqRule,
  operators: DqOperator[],
): { property: string; op: string; value: string; action: 'accept' | 'reject' } {
  const definition = rule.definition ?? {}
  const spec = operators.find((o) => o.op === definition.op)
  const value = Object.entries(definition)
    .filter(([key]) => key !== 'op')
    .map(([, raw]) => (Array.isArray(raw) ? raw.join(', ') : String(raw)))
    .join(' · ')
  return {
    property: rule.property_key ?? '(whole record)',
    op: spec?.label ?? String(definition.op ?? '—'),
    value,
    action: definition.action === 'reject' ? 'reject' : 'accept',
  }
}

/** Try the whole rule set against a made-up record, saving nothing. */
function RuleTester({
  detail,
  report,
  busy,
  onRun,
}: {
  detail: NodeTypeDetail
  report: DqReport | null
  busy: boolean
  onRun: (record: Record<string, unknown>) => void
}) {
  const [values, setValues] = useState<Record<string, string>>({})

  return (
    <div className="card stack">
      <SectionHeader
        title="Try it"
        hint="Run this node type's rules against a sample record. Nothing is saved."
      />

      <div className="stack" style={{ gap: 8 }}>
        {detail.properties.slice(0, 12).map((property) => (
          <div className="field" key={property.property_id}>
            <label className="field-label mono" style={{ fontSize: 12 }}>
              {property.key}
              {property.is_required && <span className="field-req">*</span>}
            </label>
            <input
              className="input"
              value={values[property.key] ?? ''}
              placeholder={property.data_type}
              onChange={(e) => setValues((v) => ({ ...v, [property.key]: e.target.value }))}
            />
          </div>
        ))}
        {detail.properties.length === 0 && (
          <p className="small faint">Add properties first — there is nothing to fill in yet.</p>
        )}
      </div>

      <button
        className="btn btn-primary"
        disabled={busy || detail.properties.length === 0}
        onClick={() =>
          onRun(
            Object.fromEntries(
              Object.entries(values).filter(([, v]) => v !== ''),
            ) as Record<string, unknown>,
          )
        }
      >
        {busy ? 'Running…' : 'Run the rules'}
      </button>

      {report && (
        <div className="stack" style={{ gap: 8 }}>
          <div className="row-tight">
            <span
              className={`badge ${report.has_errors ? 'badge-danger' : 'badge-ok'}`}
            >
              {report.has_errors ? 'Would be quarantined' : 'Would pass'}
            </span>
            <span className="small muted">
              {report.passed} passed · score {(report.score * 100).toFixed(0)}%
            </span>
          </div>

          {report.violations.map((violation) => (
            <div key={violation.rule_id} className="row-tight small">
              <SeverityChip severity={violation.severity as DqSeverity} />
              <span className="mono">{violation.property_key ?? '(record)'}</span>
              <span className="muted">{violation.detail}</span>
            </div>
          ))}

          {Object.keys(report.derived).length > 0 && (
            <div className="callout callout-accent">
              <p className="small" style={{ margin: 0 }}>
                Filled in by INFER/COMPUTE rules:{' '}
                <span className="mono">{JSON.stringify(report.derived)}</span>
              </p>
            </div>
          )}

          {report.access.length > 0 && (
            <p className="small faint" style={{ margin: 0 }}>
              Masked on read: {report.access.map((a) => a.property_key).join(', ')}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function DqRuleDialog({
  graphId,
  nodeTypeId,
  detail,
  existing,
  onClose,
}: {
  graphId: string
  nodeTypeId: string
  detail: NodeTypeDetail
  existing?: DqRule
  onClose: () => void
}) {
  const { create, update } = useDqMutations(graphId, nodeTypeId)
  const toast = useToast()

  const catalogue = useDqConditionCatalogue(graphId)

  const [name, setName] = useState(existing?.name ?? '')

  // The stored definition, read back into the builder. A rule written before
  // conditions existed has no `conditions` array; it opens with one empty row
  // rather than with nothing, so it can be rewritten in place.
  const stored = (existing?.definition ?? {}) as Partial<DqDefinition>
  const firstProperty = detail.properties[0]?.key ?? ''

  const [action, setAction] = useState<'accept' | 'reject'>(
    stored.action === 'reject' ? 'reject' : 'accept',
  )
  const [matchAll, setMatchAll] = useState(stored.match !== 'any')
  const [conditions, setConditions] = useState<DqCondition[]>(() =>
    Array.isArray(stored.conditions) && stored.conditions.length
      ? stored.conditions
      : [{ property: firstProperty, op: '', value: '' }],
  )

  /** The operators worth offering for a property, by its declared type. */
  function opsFor(propertyKey: string): string[] {
    const type = detail.properties.find((p) => p.key === propertyKey)?.data_type
    return catalogue.data?.by_type[type ?? ''] ?? catalogue.data?.default ?? []
  }
  const takesValue = (op: string) =>
    catalogue.data?.operators.find((o) => o.op === op)?.takes_value ?? true
  const labelFor = (op: string) =>
    catalogue.data?.operators.find((o) => o.op === op)?.label ?? op

  function edit(index: number, patch: Partial<DqCondition>) {
    setConditions((list) => list.map((c, i) => (i === index ? { ...c, ...patch } : c)))
  }

  // Every row needs a property and an operator to mean anything.
  const incomplete = conditions.some((c) => !c.property || !c.op)
  const busy = create.isPending || update.isPending

  async function save() {
    const body = {
      // Derived from the name rather than asked for: the key only ever mattered
      // to the API, and the backend normalises it.
      key: name.trim(),
      name: name.trim(),
      kind: 'VALIDATE',
      // Every violation rejects the record — there is no "warn but let it
      // through" any more, so the severity is not a choice the user makes.
      severity: 'ERROR',
      // Kept in step with the first condition so the rule still reports which
      // property it is about in listings that read `property_key`.
      property_key: conditions[0]?.property || null,
      definition: {
        match: matchAll ? 'all' : 'any',
        action,
        conditions: conditions.map((c) => ({
          property: c.property,
          op: c.op,
          ...(takesValue(c.op) ? { value: c.value ?? '' } : {}),
        })),
      },
    }
    try {
      if (existing) await update.mutateAsync({ id: existing.rule_id, body })
      else await create.mutateAsync(body)
      toast.success('Rule saved.')
      onClose()
    } catch (error) {
      toast.error(error)
    }
  }

  return (
    <Modal
      title={existing ? `Edit ${existing.name}` : 'New quality rule'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={save}
            disabled={busy || !name.trim() || incomplete}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="small muted" style={{ margin: '-4px 0 4px' }}>
          Define the check this rule runs, and what happens to a record that fails it.
        </p>

        <Field label="Rule name" required>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Email format validation"
          />
        </Field>

        {/* The rule belongs to the node type this page is already scoped to, so
            it is shown rather than chosen — picking a different one here would
            not move the rule. */}
        <Field label="Node type">
          <span className="cp-token" style={{ width: '100%' }}>
            {detail.node_type.key}
          </span>
        </Field>

        <div className="cp-sectitle">
          <span>Conditions</span>
          <button
            type="button"
            className="cp-addlink"
            onClick={() =>
              setConditions((list) => [...list, { property: firstProperty, op: '', value: '' }])
            }
          >
            + Add
          </button>
        </div>

        {/* How the rows combine. Only worth asking once there are two. */}
        {conditions.length > 1 && (
          <div className="cp-actionrow" style={{ marginTop: 0 }}>
            Records must match
            <div className="cp-seg" role="group" aria-label="Combine conditions">
              <button
                type="button"
                className="cp-segbtn"
                data-on={matchAll}
                aria-pressed={matchAll}
                onClick={() => setMatchAll(true)}
              >
                all
              </button>
              <button
                type="button"
                className="cp-segbtn"
                data-on={!matchAll}
                aria-pressed={!matchAll}
                onClick={() => setMatchAll(false)}
              >
                any
              </button>
            </div>
            of these
          </div>
        )}

        {catalogue.isLoading && <p className="small faint">Loading the comparisons…</p>}

        {conditions.map((condition, index) => {
          const available = opsFor(condition.property)
          return (
            <div className="cp-cond" key={index}>
              <div className="cp-condrow">
                {/* Changing the property can strand the operator — an integer
                    has no "contains" — so an operator that no longer applies
                    is cleared rather than left showing something untrue. */}
                <select
                  className="select"
                  aria-label="Property"
                  value={condition.property}
                  onChange={(e) => {
                    const property = e.target.value
                    const stillValid = opsFor(property).includes(condition.op)
                    edit(index, { property, op: stillValid ? condition.op : '' })
                  }}
                >
                  {detail.properties.map((property) => (
                    <option key={property.property_id} value={property.key}>
                      {property.key} ({property.data_type})
                    </option>
                  ))}
                </select>

                <select
                  className="select"
                  aria-label="Comparison"
                  value={condition.op}
                  onChange={(e) => edit(index, { op: e.target.value })}
                >
                  <option value="">choose…</option>
                  {available.map((op) => (
                    <option key={op} value={op}>
                      {labelFor(op)}
                    </option>
                  ))}
                </select>

                {condition.op && takesValue(condition.op) ? (
                  <input
                    className="input"
                    aria-label="Value"
                    placeholder="value"
                    value={String(condition.value ?? '')}
                    onChange={(e) => edit(index, { value: e.target.value })}
                  />
                ) : (
                  <span className="cp-token" data-v="empty">
                    no value needed
                  </span>
                )}

                <button
                  type="button"
                  className="cp-condrm"
                  aria-label="Remove this condition"
                  disabled={conditions.length === 1}
                  onClick={() => setConditions((list) => list.filter((_, i) => i !== index))}
                >
                  ✕
                </button>
              </div>
            </div>
          )
        })}

        {/* The verdict. `accept` means the conditions describe a valid record,
            so failing them is the violation; `reject` is the mirror image. */}
        <div className="cp-cond">
          <div className="cp-actionrow" style={{ marginTop: 0 }}>
            If a record matches, it is:
            <div className="cp-seg" role="group" aria-label="Action">
              <button
                type="button"
                className="cp-segbtn"
                data-on={action === 'accept'}
                data-tone="ok"
                aria-pressed={action === 'accept'}
                onClick={() => setAction('accept')}
              >
                Accepted
              </button>
              <button
                type="button"
                className="cp-segbtn"
                data-on={action === 'reject'}
                data-tone="err"
                aria-pressed={action === 'reject'}
                onClick={() => setAction('reject')}
              >
                Rejected
              </button>
            </div>
          </div>
          <p className="cp-condhelp">
            {action === 'accept'
              ? 'A record that matches goes on to the match rules, and to golden records. One that fails is rejected.'
              : 'A record that matches is rejected. One that does not goes on to the match rules, and to golden records.'}
          </p>
        </div>

      </div>
    </Modal>
  )
}
